<script setup lang="ts">
import type { ScheduleEvent, Team } from '~/types'

defineOptions({
  name: 'ScheduleEventsTeamItem',
})

interface Props {
  scheduleEvent: ScheduleEvent.Model
  currentTeam: Team.Model
}

const props = defineProps<Props>()

const { scheduleEventBackground, styles } = useTeam(props.currentTeam)
const {
  hasScheduleEventBroadcast,
  hasScheduleEventScheduleViewLinks,
  id,
  isCurrentTeamAwayTeam,
  opponentTeamInfo,
  scheduleEventBroadcasts,
  scheduleEventScheduleViewLinks,
  sponsor,
  vsAtData,
} = useScheduleEvent(props.scheduleEvent, props.currentTeam)
</script>

<template>
  <div
    :style="styles"
    class="schedule-event"
  >
    <DashboardLink
      v-if="id"
      class="schedule-event__dashboard-link"
      :entity-id="id"
      entity-name="schedule-events"
    />
    <div
      class="schedule-event__inner"
      :class="{ 'schedule-event__inner--home': !isCurrentTeamAwayTeam }"
    >
      <div
        v-if="!isCurrentTeamAwayTeam && scheduleEventBackground"
        class="schedule-event__background-logo"
      >
        <UIImage
          :image="scheduleEventBackground"
          class="schedule-event__background-logo-image"
        />
      </div>
      <div class="schedule-event__opponent-logo">
        <UIImage
          v-if="opponentTeamInfo?.logo"
          :image="opponentTeamInfo.logo"
          class="schedule-event__opponent-logo-image"
        />
      </div>
      <div class="schedule-event__title">
        <ScheduleEventsTeamTime
          :schedule-event
          :team="currentTeam"
          class="schedule-event__date"
        />
        <span
          v-if="opponentTeamInfo"
          class="schedule-event__name"
        >
          <span
            v-if="vsAtData"
            class="schedule-event__vs-at"
          >
            {{ vsAtData }}
          </span>
          {{ opponentTeamInfo.name }}
        </span>
      </div>
      <ScheduleEventsAllLocation
        :schedule-event
        class="schedule-event__place"
      />
      <ScheduleEventsSponsor
        v-if="sponsor"
        :sponsor
        is-vertical
        class="schedule-event__sponsor"
      />
      <div class="schedule-event__meta">
        <div
          v-if="hasScheduleEventBroadcast"
          class="schedule-event__logos"
        >
          <ScheduleEventsBroadcast :broadcasts="scheduleEventBroadcasts" />
        </div>
        <div
          v-if="hasScheduleEventScheduleViewLinks"
          class="schedule-event__links"
        >
          <ScheduleEventsAllLinks :links="scheduleEventScheduleViewLinks" />
        </div>
        <ScheduleEventsAllResult
          :schedule-event
          :current-team="currentTeam"
          class="schedule-event__results"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.schedule-event {
  @apply mb-[30px] w-full relative;
  @apply xl:mb-[45px];

  &__heading {
    @apply mb-[15px];
    @apply xl:mb-[20px];

    &-title {
      @apply text-[18px] text-primary/60 font-medium leading-[1];
    }
  }

  &__inner {
    @apply flex flex-col items-center justify-center border border-primary/20 pt-[25px] rounded-[10px] relative z-[1];
    @apply lg:flex-row lg:justify-start lg:py-[20px] lg:px-[15px];
    @apply xl:py-[30px] xl:px-[35px];
  }

  &__opponent-logo {
    @apply shrink-0 w-[50px] h-[50px] mb-[20px] rounded-full border border-primary/20 flex items-center justify-center;
    @apply lg:mr-[15px] lg:mb-0;
    @apply xl:mr-[30px] xl:w-[60px] xl:h-[60px];

    &-image {
      @apply w-[80%] h-[80%] object-contain block;
    }
  }

  &__title {
    @apply flex flex-col items-center w-full;
    @apply lg:w-[300px] lg:items-start;
    @apply xl:w-[430px];
  }

  &__date,
  &__venue {
    @apply text-[14px] text-primary/60 font-medium leading-[1] mb-[5px];
    @apply md:text-[16px];
    @apply xl:text-[18px];
  }

  &__name,
  &__location,
  &__vs-at {
    @apply text-[20px] text-primary font-medium leading-[1];
    @apply md:text-[22px];
    @apply xl:text-[28px];
  }

  :deep(.schedule-event-location__address),
  :deep(.schedule-event-sponsor__title) {
    @apply text-[14px] text-primary/60 font-medium leading-[1];
    @apply md:text-[16px];
    @apply lg:mb-[5px];
    @apply xl:text-[18px];
  }

  :deep(.schedule-event-location__place),
  :deep(.schedule-event-sponsor__button) {
    @apply text-[20px] text-primary font-medium leading-[1];
    @apply md:text-[22px];
    @apply xl:text-[28px];
  }

  :deep(.schedule-event-sponsor__button) {
    @apply h-auto relative;
    @apply md:h-[22px] md:block;
    @apply xl:h-[28px];

    span {
      @apply md:absolute md:block md:top-0 md:left-0 md:w-full md:h-full md:text-center;
    }
  }

  :deep(.schedule-event-sponsor__image) {
    @apply max-h-[70px] max-w-[120px];
    @apply md:inline-block md:max-h-[40px] md:max-w-[90px];
    @apply xl:max-h-[50px] xl:max-w-[100px]
  }

  &__sponsor {
    @apply mt-[20px];
    @apply lg:ml-[30px] lg:mt-0;
  }

  &__venue,
  &__location {
    @apply max-lg:text-[16px];
    @apply max-md:text-[14px];
  }

  &__place {
    @apply w-full flex flex-col items-center text-left gap-[6px] mt-[10px];
    @apply lg:gap-0 lg:mt-0 lg:items-start;
    @apply lg:w-[230px];
    @apply xl:w-[300px];
  }

  &__meta {
    @apply flex items-center flex-wrap justify-center gap-[10px] mt-[25px] px-[20px] py-[25px] border-t border-t-primary/20 w-full empty:py-0 empty:border-0;
    @apply lg:ml-auto lg:pl-[30px] lg:mt-0 lg:py-0 lg:border-none lg:w-auto lg:justify-end lg:pr-0;
  }

  &__logos {
    @apply lg:mr-[10px] flex gap-5;
    @apply xl:mr-[40px];
  }

  &__logo {
    @apply w-auto h-auto max-w-[100px] max-h-[50px];
  }

  &__results {
    @apply text-[18px] font-medium text-primary inline-flex items-center w-max;
  }

  &__inner--home {
    @apply ring-[4px] ring-accent ring-inset border-transparent;
  }

  &__background-logo {
    @apply hidden absolute inset-[4px] w-[calc(100%-8px)] h-[calc(100%-8px)] -z-[1] rounded-[10px] overflow-hidden;
    @apply lg:block;

    &-image {
      @apply w-full h-full object-cover object-center;
    }
  }

  &__dashboard-link {
    @apply absolute top-0 right-0 z-[2];
  }

  &__links {
    @apply flex flex-wrap justify-center gap-[5px];
  }
}
</style>
